import type {
  CellState,
  Coord,
  LevelDef,
  PartialCellLayout,
  PieceId,
  PieceKind,
  PrefillCell,
} from '../core/types';
import { keyOf } from '../core/types';
import { Rng } from '../core/rng';

let nextPieceId = 1;

export function resetPieceIds(start = 1): void {
  nextPieceId = start;
}

export function makePiece(
  kind: PieceKind,
  extras: Partial<Omit<PieceId, 'id' | 'kind'>> = {}
): PieceId {
  return { id: nextPieceId++, kind, ...extras };
}

export class Grid {
  readonly rows: number;
  readonly cols: number;
  cells: CellState[][];

  constructor(rows: number, cols: number) {
    this.rows = rows;
    this.cols = cols;
    this.cells = [];
    for (let r = 0; r < rows; r++) {
      const row: CellState[] = [];
      for (let c = 0; c < cols; c++) {
        row.push(emptyCell());
      }
      this.cells.push(row);
    }
  }

  inBounds(r: number, c: number): boolean {
    return r >= 0 && c >= 0 && r < this.rows && c < this.cols;
  }

  get(r: number, c: number): CellState | null {
    if (!this.inBounds(r, c)) return null;
    return this.cells[r]![c]!;
  }

  active(r: number, c: number): boolean {
    const cell = this.get(r, c);
    return !!cell?.active;
  }

  pieceAt(r: number, c: number): PieceId | null {
    return this.get(r, c)?.piece ?? null;
  }

  setPiece(r: number, c: number, piece: PieceId | null): void {
    const cell = this.get(r, c);
    if (cell && cell.active) cell.piece = piece;
  }

  forEachActive(fn: (r: number, c: number, cell: CellState) => void): void {
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const cell = this.cells[r]![c]!;
        if (cell.active) fn(r, c, cell);
      }
    }
  }

  clone(): Grid {
    const g = new Grid(this.rows, this.cols);
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        g.cells[r]![c] = cloneCell(this.cells[r]![c]!);
      }
    }
    return g;
  }

  /** Serialize piece kinds for hashing / tests */
  fingerprint(): string {
    const parts: string[] = [];
    this.forEachActive((r, c, cell) => {
      const p = cell.piece;
      parts.push(
        `${r},${c}:${p ? `${p.kind}${p.special ? '*' + p.special : ''}${p.isRelic ? 'R' : ''}` : '-'}`
      );
    });
    return parts.join('|');
  }
}

function emptyCell(): CellState {
  return {
    active: true,
    piece: null,
    blocker: null,
    under: null,
    spawnAllowed: true,
    isExit: false,
    sealed: false,
  };
}

function cloneCell(c: CellState): CellState {
  return {
    active: c.active,
    piece: c.piece ? { ...c.piece } : null,
    blocker: c.blocker ? { ...c.blocker } : null,
    under: c.under ? { ...c.under } : null,
    portalTo: c.portalTo ? { ...c.portalTo } : undefined,
    conveyorDir: c.conveyorDir,
    oneWay: c.oneWay,
    spawnAllowed: c.spawnAllowed,
    isExit: c.isExit,
    sealed: c.sealed,
  };
}

export function applyLayout(grid: Grid, layout?: Record<string, PartialCellLayout>): void {
  if (!layout) return;
  for (const [k, partial] of Object.entries(layout)) {
    const [rs, cs] = k.split(',');
    const r = Number(rs);
    const c = Number(cs);
    const cell = grid.get(r, c);
    if (!cell) continue;
    if (partial.hole) {
      cell.active = false;
      cell.piece = null;
      cell.blocker = null;
      cell.under = null;
      continue;
    }
    if (partial.portalTo) cell.portalTo = { ...partial.portalTo };
    if (partial.conveyorDir) cell.conveyorDir = partial.conveyorDir;
    if (partial.oneWay) cell.oneWay = partial.oneWay;
    if (partial.noSpawn) cell.spawnAllowed = false;
    if (partial.exit) cell.isExit = true;
    if (partial.blocker) cell.blocker = { ...partial.blocker };
    if (partial.under) cell.under = { ...partial.under };
    if (partial.sealed) cell.sealed = true;
  }
}

export function applyPrefill(grid: Grid, prefill?: Record<string, PrefillCell>): void {
  if (!prefill) return;
  for (const [k, pf] of Object.entries(prefill)) {
    const [rs, cs] = k.split(',');
    const r = Number(rs);
    const c = Number(cs);
    const cell = grid.get(r, c);
    if (!cell?.active) continue;
    if (pf.kind) {
      cell.piece = makePiece(pf.kind, {
        special: pf.special,
        beamAxis: pf.beamAxis,
        isRelic: pf.isRelic,
      });
    } else if (pf.isRelic) {
      // relic needs a kind underneath - skip if no kind
    }
  }
}

/** Fill empty active cells without creating matches when forbidMatches is true. */
export function fillBoard(
  grid: Grid,
  pieceSet: PieceKind[],
  rng: Rng,
  weights?: Partial<Record<PieceKind, number>>,
  forbidMatches = true,
  maxAttempts = 40
): void {
  const w = pieceSet.map((k) => weights?.[k] ?? 1);

  grid.forEachActive((r, c, cell) => {
    if (cell.piece) return;
    if (cell.blocker && (cell.blocker.type === 'crate' || cell.blocker.type === 'frost')) {
      // blockers can sit under pieces or replace them — crates/frost occupy cell
      if (cell.blocker.type === 'crate') return;
    }
    let kind: PieceKind = rng.weighted(pieceSet, w);
    if (forbidMatches) {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        kind = rng.weighted(pieceSet, w);
        if (!wouldMatchAt(grid, r, c, kind)) break;
      }
    }
    cell.piece = makePiece(kind);
  });
}

function wouldMatchAt(grid: Grid, r: number, c: number, kind: PieceKind): boolean {
  // horizontal
  let left = 0;
  for (let cc = c - 1; cc >= 0; cc--) {
    const p = grid.pieceAt(r, cc);
    if (p && p.kind === kind) left++;
    else break;
  }
  let right = 0;
  for (let cc = c + 1; cc < grid.cols; cc++) {
    const p = grid.pieceAt(r, cc);
    if (p && p.kind === kind) right++;
    else break;
  }
  if (left + right >= 2) return true;

  let up = 0;
  for (let rr = r - 1; rr >= 0; rr--) {
    const p = grid.pieceAt(rr, c);
    if (p && p.kind === kind) up++;
    else break;
  }
  let down = 0;
  for (let rr = r + 1; rr < grid.rows; rr++) {
    const p = grid.pieceAt(rr, c);
    if (p && p.kind === kind) down++;
    else break;
  }
  if (up + down >= 2) return true;

  // 2x2 square involving this cell
  const corners = [
    [
      { r, c },
      { r, c: c - 1 },
      { r: r - 1, c },
      { r: r - 1, c: c - 1 },
    ],
    [
      { r, c },
      { r, c: c + 1 },
      { r: r - 1, c },
      { r: r - 1, c: c + 1 },
    ],
  ];
  for (const cells of corners) {
    let same = 0;
    let ok = true;
    for (const cell of cells) {
      if (cell.r === r && cell.c === c) {
        same++;
        continue;
      }
      const p = grid.pieceAt(cell.r, cell.c);
      if (!p || !grid.active(cell.r, cell.c)) {
        ok = false;
        break;
      }
      if (p.kind === kind) same++;
      else {
        ok = false;
        break;
      }
    }
    if (ok && same === 4) return true;
  }
  return false;
}

export function createGridFromLevel(level: LevelDef, rng: Rng): Grid {
  resetPieceIds(1);
  const grid = new Grid(level.rows, level.cols);
  applyLayout(grid, level.layout);
  applyPrefill(grid, level.prefill);
  // mark bottom row exits if any drop objectives
  if (level.objectives.some((o) => o.type === 'drop')) {
    for (let c = 0; c < grid.cols; c++) {
      const cell = grid.get(grid.rows - 1, c);
      if (cell?.active) cell.isExit = true;
    }
  }
  fillBoard(
    grid,
    level.pieceSet,
    rng,
    level.spawnWeights,
    level.forbidInitialMatches !== false
  );
  return grid;
}

export function neighbors4(r: number, c: number): Coord[] {
  return [
    { r: r - 1, c },
    { r: r + 1, c },
    { r, c: c - 1 },
    { r, c: c + 1 },
  ];
}

export { keyOf };
